import { Injectable, signal } from '@angular/core';
import { NkTreeviewNode } from './treeview.utils';

@Injectable()
export class TreeviewService<T> {
  /**
   * Stores the original unfiltered tree structure for resets.
   */
  private _originalNodes: NkTreeviewNode<T>[] = [];

  /**
   * Lookup table for fast node retrieval by ID.
   */
  private _nodeLookup: Record<string, NkTreeviewNode<T>> = {};

  /**
   * Signal to manage the current list of nodes in the tree.
   */
  private _nodes = signal<NkTreeviewNode<T>[]>([]);

  /**
   * Publicly accessible read-only signal of the current tree nodes.
   */
  public readonly nodes = this._nodes.asReadonly();

  /**
   * Signal to manage the list of currently selected nodes.
   */
  private _selectedNodes = signal<NkTreeviewNode<T>[]>([]);

  /**
   * Publicly accessible read-only signal of selected nodes.
   */
  public readonly selectedNodes = this._selectedNodes.asReadonly();

  /**
   * Initializes the tree with the provided nodes.
   *
   * @param nodes The tree structure to initialize.
   */
  public initializeTree(nodes: NkTreeviewNode<T>[]): void {
    this._originalNodes = nodes;
    this._initializeLookup(nodes);
    this._nodes.set(nodes);
  }

  /**
   * Constructs a lookup table for fast node retrieval.
   *
   * @param nodes The nodes to include in the lookup table.
   */
  private _initializeLookup(nodes: NkTreeviewNode<T>[]): void {
    const buildLookup = (nodes: NkTreeviewNode<T>[]): void => {
      for (const node of nodes) {
        this._nodeLookup[node.id] = node;
        if (node.children.length > 0) {
          buildLookup(node.children);
        }
      }
    };
    buildLookup(nodes);
  }

  /**
   * Toggles the checked state of a node and updates related states.
   *
   * @param nodeId The ID of the node to toggle.
   * @param isChecked The new checked state.
   */
  public toggleChecked(nodeId: string, isChecked: boolean): void {
    const updateFn = (node: NkTreeviewNode<T>): void => {
      if (node.id === nodeId || this._isDescendantOf(node, nodeId)) {
        node.checked = isChecked;
        node.indeterminate = false;
      }
    };

    this._nodes.set(this._updateNodes(this._nodes(), updateFn));
    this._updateParentStates();
    this._emitSelection();
  }

  /**
   * Checks if a node is a descendant of a target node.
   *
   * @param node The node to check.
   * @param targetId The ID of the target node.
   * @returns `true` if the node is a descendant of the target node, otherwise `false`.
   */
  private _isDescendantOf(node: NkTreeviewNode<T>, targetId: string): boolean {
    if (!node.parentId) {
      // Root node
      return false;
    }
    if (node.parentId === targetId) {
      return true;
    }
    const parent = this._findNodeById(node.parentId);
    return parent ? this._isDescendantOf(parent, targetId) : false;
  }

  /**
   * Toggles the expanded state of a node.
   *
   * @param nodeId The ID of the node to toggle.
   * @param isExpanded The new expanded state.
   */
  public toggleExpand(nodeId: string, isExpanded: boolean): void {
    const updateNodeExpanded = (node: NkTreeviewNode<T>): void => {
      if (node.id === nodeId) {
        node.expanded = isExpanded;
      }
    };
    this._nodes.set(this._updateNodes(this._nodes(), updateNodeExpanded));
  }

  /**
   * Expands or collapses all nodes.
   *
   * @param isExpanded `true` to expand all nodes, `false` to collapse them.
   */
  public setAllNodesExpanded(isExpanded: boolean): void {
    const updateNode = (node: NkTreeviewNode<T>): void => {
      node.expanded = isExpanded;
      for (const child of node.children) {
        updateNode(child);
      }
    };
    this._nodes.set(this._updateNodes(this._nodes(), updateNode));
  }

  /**
   * Collapses all nodes and restores the original tree.
   */
  public collapseAllOnEmptySearch(): void {
    this._nodes.set(this._originalNodes);
    this.setAllNodesExpanded(false);
  }

  public setAllNodesChecked(isChecked: boolean): void {
    const updateNode = (node: NkTreeviewNode<T>): void => {
      node.checked = isChecked;
      node.indeterminate = false;
      for (const child of node.children) {
        updateNode(child);
      }
    };
    this._nodes.set(this._updateNodes(this._nodes(), updateNode));
    this._emitSelection();
  }

  /**
   * Filters the nodes based on a custom function.
   *
   * @param filterFn A function to determine whether a node matches the filter.
   */
  public filterNodes(filterFn: (node: NkTreeviewNode<T>) => boolean): void {
    const filterNodes = (nodes: NkTreeviewNode<T>[]): NkTreeviewNode<T>[] => {
      return nodes
        .map((node) => {
          const filteredChildren = filterNodes(node.children);
          const matches = filterFn(node);
          if (matches || filteredChildren.length > 0) {
            return {
              ...node,
              children: filteredChildren,
              expanded: filteredChildren.length > 0,
            };
          }
          return null;
        })
        .filter((node): node is NkTreeviewNode<T> => node !== null);
    };

    this._nodes.set(filterNodes(this._originalNodes));
  }

  private _findNodeById(nodeId: string): NkTreeviewNode<T> | null {
    return this._nodeLookup[nodeId] || null;
  }

  /**
   * Updates parent nodes' checked and indeterminate states.
   */
  private _updateParentStates(): void {
    const updateParentState = (node: NkTreeviewNode<T>): void => {
      if (node.children.length > 0) {
        const allChecked = node.children.every((child) => child.checked);
        const noneChecked = node.children.every((child) => !child.checked && !child.indeterminate);
        node.checked = allChecked;
        node.indeterminate = !allChecked && !noneChecked;

        if (node.parentId) {
          const parent = this._findNodeById(node.parentId);
          if (parent) {
            updateParentState(parent);
          }
        }
      }
    };

    for (const node of this._nodes()) {
      if (!node.parentId) {
        updateParentState(node);
      }
    }
  }

  /**
   * Recursively updates nodes using a provided update function.
   *
   * @param currentNodes The nodes to update.
   * @param updateFn The function to apply to each node.
   * @returns The updated nodes.
   */
  private _updateNodes(
    currentNodes: NkTreeviewNode<T>[],
    updateFn: (node: NkTreeviewNode<T>) => void,
  ): NkTreeviewNode<T>[] {
    const updateParentFn = (node: NkTreeviewNode<T>): void => {
      if (node && node.parentId) {
        const parentNode = this._findNodeById(node.parentId);
        if (parentNode) {
          const allChecked = parentNode.children.every((child) => child.checked);
          const noneChecked = parentNode.children.every((child) => !child.checked && !child.indeterminate);

          parentNode.checked = allChecked;
          parentNode.indeterminate = !allChecked && !noneChecked;

          updateParentFn(parentNode);
        }
      }
    };
    return currentNodes.map((node) => {
      // Apply the update to the current node
      updateFn(node);

      // Recursively apply the update to children
      node.children = this._updateNodes(node.children, updateFn);
      updateParentFn(node);

      return node;
    });
  }

  /**
   * Emits the list of selected nodes.
   */
  private _emitSelection(): void {
    const selectedNodeDict: { [nodeId: string]: NkTreeviewNode<T> } = {};

    const collectSelectedFn = (nodes: NkTreeviewNode<T>[]): void => {
      for (const node of nodes) {
        if (node.checked) {
          selectedNodeDict[node.id] = node;
        }
        collectSelectedFn(node.children);
      }
    };

    collectSelectedFn(this._nodes());
    this._selectedNodes.set(Object.values(selectedNodeDict));
  }
}
